/**
 * Step 2 — contact (lié à la company sélectionnée) + courtier assigné.
 *
 * Le contact se choisit dans une liste filtrée par company_id. Si la
 * company n'a pas de contact, on affiche un message + lien vers la fiche
 * pour en créer un.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { BrokerPicker } from "../../../components/BrokerPicker";
import type { IntakeMvpData } from "../../../lib/intakeDraftStorage";
import { useContacts } from "../../../lib/queries/contacts";
import { useCourtierParDefaut } from "../../../lib/queries/projects";

type Props = {
  data: IntakeMvpData;
  onChange: (next: Partial<IntakeMvpData>) => void;
};

export function Step2Contacts({ data, onChange }: Props) {
  const { t } = useTranslation();
  const contacts = useContacts(data.company_id, { limit: 50 });

  /**
   * 9.BD.1 — le courtier est PROPOSÉ, pas cherché.
   *
   * L'assistant faisait chercher une réponse que la base connaît : l'entreprise
   * porte souvent son conseiller, et dans un petit cabinet c'est l'utilisateur
   * lui-même. Le serveur décide qui ; l'écran ne fait que le montrer, avec son
   * origine — un champ pré-rempli dont on ignore la provenance est un champ
   * qu'on n'ose pas corriger.
   *
   * Le défaut ne s'applique qu'à un champ JAMAIS touché : `touche` retient le
   * geste de l'utilisateur, sinon effacer volontairement le courtier le verrait
   * réapparaître à la frappe suivante.
   */
  const defaut = useCourtierParDefaut(data.company_id);
  const [touche, setTouche] = useState(false);
  useEffect(() => {
    if (touche || data.broker_id || !defaut.data?.broker_id) return;
    onChange({ broker_id: defaut.data.broker_id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaut.data?.broker_id, touche]);

  const origine =
    !touche && data.broker_id && data.broker_id === defaut.data?.broker_id
      ? defaut.data.origin
      : null;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold tracking-tight text-slate-900">
          {t("projects.intake.step2.title")}
        </h2>
        <p className="mt-1 text-sm text-slate-600">{t("projects.intake.step2.subtitle")}</p>
      </div>

      <label className="block">
        <span className="label">{t("projects.fields.contact")}</span>
        {contacts.isLoading && !contacts.data ? (
          <p className="text-sm text-slate-500">{t("app.loading")}</p>
        ) : contacts.data && contacts.data.items.length === 0 ? (
          <p className="text-sm text-slate-600">
            {t("projects.intake.step2.no_contact")}{" "}
            {data.company_id && (
              <Link
                to={`/companies/${data.company_id}`}
                className="text-brand-700 focus-visible:ring-brand-500 rounded-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              >
                {t("projects.intake.step2.go_create_contact")}
              </Link>
            )}
          </p>
        ) : (
          <select
            className="input"
            value={data.contact_id ?? ""}
            onChange={(e) => onChange({ contact_id: e.target.value || undefined })}
          >
            <option value="">{t("projects.intake.step2.no_contact_selected")}</option>
            {contacts.data?.items.map((c) => (
              <option key={c.id} value={c.id}>
                {c.full_name}
                {c.title ? ` — ${c.title}` : ""}
                {c.is_primary ? ` ★` : ""}
              </option>
            ))}
          </select>
        )}
      </label>

      <div>
        <span className="label">{t("projects.fields.broker")}</span>
        <BrokerPicker
          value={data.broker_id}
          onChange={(id) => {
            setTouche(true);
            onChange({ broker_id: id });
          }}
        />
        {origine && (
          <p className="mt-1 text-xs text-slate-500">
            {origine === "company_primary"
              ? t("projects.intake.step2.broker_from_company")
              : t("projects.intake.step2.broker_from_you")}
          </p>
        )}
      </div>
    </div>
  );
}
